import { Action, Store } from '@ngrx/store';
import { catchError, from, mergeMap, Observable } from 'rxjs';
import { UpdateEventNavigatorGQL, UpdateEventNavigatorMutation } from 'src/generated/graphql.generated';

import { generateMutationContext } from '../../../core/auth/session';
import { generateUUID } from '../../../utilities/state.util';

import { NavigatorOutboxActions } from './navigator-outbox.actions';
import { navigatorOutboxFeature } from './navigator-outbox.reducer';
import { isNoSignalError, NavigatorSaveDropped, NavigatorWrite, nextNavigatorWriteSeq } from './navigator-outbox.util';

/**
 * What a reply says beyond the stored run: the event's ops revision, which later writes are based
 * on, and whether the server dropped part or all of this write because ops changed the run first
 * (#86b); for a write with inventory changes, the job's merged inventory and the items where the
 * office's version was kept (#86c). Shared by the live send and the replay.
 */
export function navigatorReplyActions(
  jobId: string | null,
  eventId: string,
  reply: UpdateEventNavigatorMutation['updateEventNavigator'] | null | undefined,
): Action[] {
  if (!reply) { return []; }
  const actions: Action[] = [];
  if (typeof reply.navigatorOpsRevision === 'number') {
    actions.push(NavigatorOutboxActions.opsRevisionSeen({ eventId, opsRevision: reply.navigatorOpsRevision }));
  }
  if (reply.navigatorSaveDropped === 'moved' || reply.navigatorSaveDropped === 'reset') {
    actions.push(NavigatorOutboxActions.saveDropped({
      jobId,
      eventId,
      reason: reply.navigatorSaveDropped as NavigatorSaveDropped,
      currentStep: reply.navigatorCurrentStep ?? null,
      navigatorData: reply.navigatorData ?? null,
    }));
  }
  if (reply.navigatorInventoryKept) {
    actions.push(NavigatorOutboxActions.inventoryMerged({
      jobId,
      eventId,
      inventory: reply.navigatorInventory ?? null,
      kept: reply.navigatorInventoryKept.map(({ id, name, reason }) => ({ id, name, reason })),
    }));
  }
  return actions;
}

/**
 * Sends one of the crew's navigator writes, or puts it in the outbox (#86a).
 *
 * Queued instead of sent when the phone is offline or earlier writes are still waiting — the
 * waiting ones go first, in order, so a later write can never be overtaken by an earlier one on
 * replay. Queued after the fact when the send fails for lack of signal. A server refusal is not
 * queued: `refused` keeps the caller's own error handling.
 *
 * Shared by every effect that writes the crew's run (`job-workorders.effects.ts`), so all of them
 * follow the same rule.
 */
export function sendOrQueueNavigatorWrite(
  store: Store,
  updateEventNavigatorGQL: UpdateEventNavigatorGQL,
  jobId: string | null,
  write: NavigatorWrite,
  handlers: {
    /** The write landed. `navigatorData` is the run as the server stored it. */
    stored: (navigatorData: string | null) => Action[];
    refused: (error: Error) => Action[];
    queued?: () => Action[];
  },
): Observable<Action> {
  const seq = nextNavigatorWriteSeq();

  // Based on the newest revision this device knows of, including one only a reply has carried.
  const known = store.selectSignal(navigatorOutboxFeature.selectKnownOpsRevision)()[write.eventId];
  if (write.basedOnOpsRevision !== undefined || known !== undefined) {
    write = { ...write, basedOnOpsRevision: Math.max(write.basedOnOpsRevision ?? 0, known ?? 0) };
  }

  const queue = (): Action[] => [
    NavigatorOutboxActions.saveQueued({ entry: { id: generateUUID(), seq, jobId, write } }),
    ...(handlers.queued?.() ?? []),
  ];

  const waiting = store.selectSignal(navigatorOutboxFeature.selectQueue)();
  if (!navigator.onLine || waiting.length > 0) { return from(queue()); }

  return updateEventNavigatorGQL.mutate({ ...write }, { context: generateMutationContext() }).pipe(
    mergeMap((result) => {
      const reply = result.data?.updateEventNavigator;
      return from([
        // A write the server cut short did not store its keys, so it must not trim older ones.
        ...(reply?.navigatorSaveDropped ? [] : [ NavigatorOutboxActions.directSaveStored({ seq, write }) ]),
        ...navigatorReplyActions(jobId, write.eventId, reply),
        ...handlers.stored(reply?.navigatorData ?? null),
      ]);
    }),
    catchError((error) => from(isNoSignalError(error) ? queue() : handlers.refused(error))),
  );
}
